import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { DataTableColumnSelector } from '@/components/data-display/data-table-column-selector'
import { Pagination } from '@/components/data-display/pagination'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { SearchInput } from '@/components/forms/search-input'
import { Select } from '@/components/forms/select'
import { FilterBar } from '@/components/layout/filter-bar'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { Button } from '@/components/primitives/button'
import { formatDateTime } from '@/features/admin/shared/format'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import actionDisableIconUrl from '@/assets/action-menu/disable.svg'
import actionEnableIconUrl from '@/assets/action-menu/enable.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import actionResetPasswordIconUrl from '@/assets/action-menu/reset-password.svg'
import actionViewIconUrl from '@/assets/action-menu/view.svg'

import { AdminMemberCreateModal } from './member-create-modal'
import { AdminMemberAccountDrawer } from './member-account-drawer'
import { AdminMemberAttributesDrawer } from './member-attributes-drawer'
import { AdminMemberResetPasswordModal } from './member-reset-password-modal'
import { TemporaryPasswordModal } from './temporary-password-modal'
import {
  batchUpdateUserStatus,
  getOrganizationTree,
  listMemberAccounts,
  listOrganizationCompanies,
  type AdminMemberAccount,
  type AdminMemberAccountsParams,
} from './api'
import { flattenDepartments, memberDepartmentSummary, normalizeDepartmentTree } from './model'
import { useOrganizationSyncStatus } from './sync-status'

type MemberStatusFilter = NonNullable<AdminMemberAccountsParams['status']>
type MemberAccountSortField = 'created_at' | 'last_active_at'
type AccountSortOrder = 'asc' | 'desc'

interface TemporaryPasswordResult {
  email: string
  generated_password?: string
  title?: string
  titleHelp?: string
}

const defaultPageSize = 10
const defaultMemberStatusFilter: MemberStatusFilter = ''
const defaultMemberAccountColumnIds = [
  'name',
  'email',
  'department',
  'role',
  'status',
  'created-at',
  'last-active-at',
  'actions',
]

function roleLabel(role: string, t: (key: string) => string) {
  if (role === 'admin') {
    return t('memberAccounts.role.admin')
  }

  if (role === 'user') {
    return t('memberAccounts.role.user')
  }

  return role.trim() ? role : t('memberAccounts.role.unknown')
}

function statusLabel(status: AdminMemberAccount['status'], t: (key: string) => string) {
  return t(`memberAccounts.status.${status}`)
}

export function AdminMemberAccountsPage() {
  const { t } = useTranslation('adminOrganization')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<MemberStatusFilter>(defaultMemberStatusFilter)
  const [sortField, setSortField] = useState<MemberAccountSortField>('last_active_at')
  const [sortOrder, setSortOrder] = useState<AccountSortOrder>('desc')
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>(defaultMemberAccountColumnIds)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null)
  const [managingMember, setManagingMember] = useState<AdminMemberAccount | null>(null)
  const [attributesMember, setAttributesMember] = useState<AdminMemberAccount | null>(null)
  const [resetPasswordMember, setResetPasswordMember] = useState<AdminMemberAccount | null>(null)
  const [createMemberOpen, setCreateMemberOpen] = useState(false)
  const [temporaryPasswordMember, setTemporaryPasswordMember] =
    useState<TemporaryPasswordResult | null>(null)

  const companiesQuery = useQuery({
    queryFn: listOrganizationCompanies,
    queryKey: ['admin-organization-companies'],
  })
  const syncStatusQuery = useOrganizationSyncStatus()
  const isSyncing = syncStatusQuery.data?.is_syncing ?? false
  const companyId =
    companiesQuery.data?.find((company) => company.id === selectedCompanyId)?.id ??
    companiesQuery.data?.[0]?.id ??
    null
  const previousCompanyIdRef = useRef<string | null>(companyId)

  useEffect(() => {
    if (previousCompanyIdRef.current !== null && previousCompanyIdRef.current !== companyId) {
      setSelectedCompanyId(companyId)
      setDepartmentFilter('')
      setPage(1)
    }
    previousCompanyIdRef.current = companyId
  }, [companyId])

  const treeQuery = useQuery({
    enabled: Boolean(companyId),
    queryFn: () => getOrganizationTree(companyId ?? ''),
    queryKey: ['admin-organization-tree', companyId],
  })
  const departmentOptions = useMemo(() => {
    const rootDepartment = treeQuery.data ? normalizeDepartmentTree(treeQuery.data) : null
    const departments = flattenDepartments(rootDepartment)

    return [
      {
        label: t('common.allDepartments'),
        value: '',
      },
      ...departments.map((department) => ({
        label: `${'  '.repeat(department.depth)}${department.name}`,
        value: department.id,
      })),
    ]
  }, [treeQuery.data, t])
  const [previousTreeData, setPreviousTreeData] = useState(treeQuery.data)
  if (treeQuery.data !== previousTreeData) {
    setPreviousTreeData(treeQuery.data)
    if (
      treeQuery.isSuccess &&
      departmentFilter !== '' &&
      !departmentOptions.some((option) => option.value === departmentFilter)
    ) {
      setDepartmentFilter('')
      setPage(1)
    }
  }

  const statusOptions = useMemo(
    () => [
      {
        label: t('common.allStatuses'),
        value: '',
      },
      {
        label: t('memberAccounts.status.active'),
        value: 'active',
      },
      {
        label: t('memberAccounts.status.banned'),
        value: 'banned',
      },
    ],
    [t],
  )
  const accountsQuery = useQuery({
    enabled: Boolean(companyId),
    queryFn: () =>
      listMemberAccounts({
        orgUnitId: departmentFilter.trim() ? departmentFilter : (companyId ?? undefined),
        page,
        pageSize,
        order: sortOrder,
        orderBy: sortField,
        search,
        status: statusFilter,
      }),
    queryKey: [
      'admin-organization-member-accounts',
      {
        departmentFilter,
        companyId,
        page,
        pageSize,
        search,
        sortField,
        sortOrder,
        statusFilter,
      },
    ],
  })

  const showOperationFeedback = (tone: 'error' | 'success' | 'warning', message: string) => {
    showFeedback({
      message,
      tone,
    })
  }
  const statusMutation = useMutation({
    mutationFn: ({
      status,
      userId,
    }: {
      status: Extract<MemberStatusFilter, 'active' | 'banned'>
      userId: string
    }) => batchUpdateUserStatus([userId], status),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: ['admin-organization-member-accounts'],
      })
      if (result.code === 'partial_success') {
        showOperationFeedback('warning', t('errors.batchStatusPartial'))
        return
      }
      showOperationFeedback('success', t('success.updateStatus'))
    },
    onError: () => showOperationFeedback('error', t('errors.submitFailed')),
  })
  const rows = accountsQuery.data?.items ?? []
  const total = accountsQuery.data?.total ?? 0
  const visibleColumnIdSet = useMemo(() => new Set(visibleColumnIds), [visibleColumnIds])
  const toggleSort = useCallback(
    (field: MemberAccountSortField) => {
      const nextOrder = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc'

      setSortField(field)
      setSortOrder(nextOrder)
      setPage(1)
    },
    [sortField, sortOrder],
  )
  const columns = useMemo<DataTableColumn<AdminMemberAccount>[]>(() => {
    const sortDirection = (field: MemberAccountSortField) =>
      sortField === field ? sortOrder : null
    const sortActionLabel = (field: MemberAccountSortField, ascKey: string, descKey: string) =>
      sortField === field && sortOrder === 'desc' ? t(ascKey) : t(descKey)

    return [
      {
        header: t('memberAccounts.table.name'),
        hidden: !visibleColumnIdSet.has('name'),
        id: 'name',
        render: (member) => <span className="font-medium">{member.name}</span>,
        visibilityLabel: t('memberAccounts.table.name'),
        width: 180,
      },
      {
        header: t('memberAccounts.table.email'),
        hidden: !visibleColumnIdSet.has('email'),
        id: 'email',
        render: (member) => member.email || '-',
        visibilityLabel: t('memberAccounts.table.email'),
        width: 260,
      },
      {
        header: t('memberAccounts.table.department'),
        hidden: !visibleColumnIdSet.has('department'),
        id: 'department',
        render: (member) => memberDepartmentSummary(member),
        visibilityLabel: t('memberAccounts.table.department'),
        width: 220,
      },
      {
        header: t('memberAccounts.table.role'),
        hidden: !visibleColumnIdSet.has('role'),
        id: 'role',
        render: (member) => roleLabel(member.role, t),
        visibilityLabel: t('memberAccounts.table.role'),
        width: 140,
      },
      {
        header: t('memberAccounts.table.status'),
        hidden: !visibleColumnIdSet.has('status'),
        id: 'status',
        render: (member) => (
          <Badge variant={member.status === 'active' ? 'success' : 'warning'}>
            {statusLabel(member.status, t)}
          </Badge>
        ),
        visibilityLabel: t('memberAccounts.table.status'),
        width: 120,
      },
      {
        header: t('memberAccounts.table.createdAt'),
        hidden: !visibleColumnIdSet.has('created-at'),
        id: 'created-at',
        render: (member) => formatDateTime(member.created_at),
        sort: {
          direction: sortDirection('created_at'),
          label: sortActionLabel(
            'created_at',
            'memberAccounts.sort.createdAtAsc',
            'memberAccounts.sort.createdAtDesc',
          ),
          onToggle: () => toggleSort('created_at'),
        },
        visibilityLabel: t('memberAccounts.table.createdAt'),
        width: 180,
      },
      {
        header: t('memberAccounts.table.lastActiveAt'),
        hidden: !visibleColumnIdSet.has('last-active-at'),
        id: 'last-active-at',
        render: (member) => formatDateTime(member.last_active_at),
        sort: {
          direction: sortDirection('last_active_at'),
          label: sortActionLabel(
            'last_active_at',
            'memberAccounts.sort.lastActiveAtAsc',
            'memberAccounts.sort.lastActiveAtDesc',
          ),
          onToggle: () => toggleSort('last_active_at'),
        },
        visibilityLabel: t('memberAccounts.table.lastActiveAt'),
        width: 180,
      },
      {
        align: 'center',
        header: t('memberAccounts.table.actions'),
        hidden: !visibleColumnIdSet.has('actions'),
        id: 'actions',
        pin: 'right',
        render: (member) =>
          isSyncing ? (
            <span className="text-[var(--color-text-muted)]">-</span>
          ) : (
            <ActionMenu
              ariaLabel={member.name}
              items={[
                {
                  iconSrc: actionEditIconUrl,
                  key: 'manage',
                  label: t('actions.manage'),
                  onSelect: () => setManagingMember(member),
                },
                {
                  iconSrc: actionResetPasswordIconUrl,
                  key: 'reset-password',
                  label: t('actions.resetPassword'),
                  onSelect: () => setResetPasswordMember(member),
                },
                {
                  iconSrc: actionViewIconUrl,
                  key: 'attributes',
                  label: t('actions.viewAttributes'),
                  onSelect: () => setAttributesMember(member),
                },
                {
                  iconSrc: member.status === 'active' ? actionDisableIconUrl : actionEnableIconUrl,
                  key: member.status === 'active' ? 'disable' : 'enable',
                  label: member.status === 'active' ? t('actions.disable') : t('actions.enable'),
                  onSelect: () =>
                    statusMutation.mutate({
                      status: member.status === 'active' ? 'banned' : 'active',
                      userId: member.id,
                    }),
                  variant: member.status === 'active' ? 'danger' : 'success',
                },
              ]}
            />
          ),
        visibilityLabel: t('memberAccounts.table.actions'),
        width: 90,
      },
    ]
  }, [isSyncing, sortField, sortOrder, statusMutation, t, toggleSort, visibleColumnIdSet])
  const columnSelectorOptions = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        label: column.visibilityLabel ?? column.id,
      })),
    [columns],
  )

  return (
    <AdminShellPage activePath="/admin/members">
      <section
        aria-label={t('memberAccounts.workspaceLabel')}
        className="flex h-full min-w-0 flex-col overflow-hidden"
        data-testid="admin-member-accounts-workspace"
      >
        <PageHeader
          actions={
            <>
              <DataTableColumnSelector
                aria-label={t('memberAccounts.columnSelector.label')}
                onValueChange={setVisibleColumnIds}
                options={columnSelectorOptions}
                value={visibleColumnIds}
              >
                {t('memberAccounts.columnSelector.trigger')}
              </DataTableColumnSelector>
              <Button disabled={isSyncing} onClick={() => setCreateMemberOpen(true)} size="sm">
                <PlusIcon />
                {t('actions.createMember')}
              </Button>
            </>
          }
          description={t('memberAccounts.description')}
          title={t('memberAccounts.title')}
        />
        {isSyncing ? (
          <div className="mx-5 mb-3 rounded-lg bg-[#fff7e6] px-4 py-3 text-sm text-[#8a5100] lg:mx-[30px]">
            {t('errors.syncReadOnly')}
          </div>
        ) : null}
        <FilterBar>
          {(companiesQuery.data?.length ?? 0) > 1 ? (
            <Select
              aria-label={t('common.selectCompany')}
              onValueChange={(value) => {
                setSelectedCompanyId(value)
                setDepartmentFilter('')
                setPage(1)
              }}
              options={(companiesQuery.data ?? []).map((company) => ({
                label: company.name,
                value: company.id,
              }))}
              value={companyId ?? ''}
            />
          ) : null}
          <SearchInput
            aria-label={t('memberAccounts.searchLabel')}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder={t('memberAccounts.searchPlaceholder')}
            value={search}
          />
          <Select
            aria-label={t('common.allDepartments')}
            onValueChange={(value) => {
              setDepartmentFilter(value)
              setPage(1)
            }}
            options={departmentOptions}
            value={departmentFilter}
          />
          <Select
            aria-label={t('common.allStatuses')}
            onValueChange={(value) => {
              setStatusFilter(value as MemberStatusFilter)
              setPage(1)
            }}
            options={statusOptions}
            value={statusFilter}
          />
        </FilterBar>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-5 lg:px-[30px]">
          {accountsQuery.isError ? (
            <ErrorState
              action={
                <Button
                  className="mt-3"
                  onClick={() => {
                    void accountsQuery.refetch()
                  }}
                  size="sm"
                  variant="secondary"
                >
                  {t('actions.retry')}
                </Button>
              }
              description={t('errors.loadDescription')}
              title={t('errors.loadTitle')}
            />
          ) : (
            <>
              <DataTable
                columns={columns}
                empty={t('memberAccounts.empty')}
                getRowKey={(member) => member.id}
                isLoading={accountsQuery.isLoading}
                rows={rows}
              />
              <Pagination
                disabled={accountsQuery.isFetching}
                labels={{
                  next: t('pagination.next'),
                  pageSize: (size) => t('pagination.pageSize', { count: size }),
                  previous: t('pagination.previous'),
                }}
                onPageChange={setPage}
                onPageSizeChange={(nextPageSize) => {
                  setPageSize(nextPageSize)
                  setPage(1)
                }}
                page={page}
                pageSize={pageSize}
                total={total}
              />
            </>
          )}
        </div>
        {managingMember && !isSyncing ? (
          <AdminMemberAccountDrawer
            key={managingMember.id}
            member={managingMember}
            onOpenChange={(open) => {
              if (!open) {
                setManagingMember(null)
              }
            }}
            open={Boolean(managingMember)}
          />
        ) : null}
        {attributesMember ? (
          <AdminMemberAttributesDrawer
            key={attributesMember.id}
            member={attributesMember}
            onOpenChange={(open) => {
              if (!open) {
                setAttributesMember(null)
              }
            }}
            open={Boolean(attributesMember)}
          />
        ) : null}
        {resetPasswordMember && !isSyncing ? (
          <AdminMemberResetPasswordModal
            key={resetPasswordMember.id}
            member={resetPasswordMember}
            onClose={() => setResetPasswordMember(null)}
            onReset={(result) =>
              setTemporaryPasswordMember({
                ...result,
                titleHelp: t('temporaryPassword.resetHelp'),
                title: t('temporaryPassword.resetTitle'),
              })
            }
            open={Boolean(resetPasswordMember)}
          />
        ) : null}
        <AdminMemberCreateModal
          onClose={() => setCreateMemberOpen(false)}
          onCreated={(result) => setTemporaryPasswordMember(result)}
          open={createMemberOpen && !isSyncing}
        />
        {temporaryPasswordMember ? (
          <TemporaryPasswordModal
            email={temporaryPasswordMember.email}
            onClose={() => setTemporaryPasswordMember(null)}
            open={Boolean(temporaryPasswordMember)}
            password={temporaryPasswordMember.generated_password}
            title={temporaryPasswordMember.title}
            titleHelp={temporaryPasswordMember.titleHelp}
          />
        ) : null}
      </section>
    </AdminShellPage>
  )
}

function PlusIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  )
}
